import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageBanner from "@/components/PageBanner";
import { Link } from "react-router-dom";
import {
  Sparkles, TrendingUp, Users, Award, ArrowRight,
  Heart, GraduationCap, Eye, Compass, Quote,
} from "lucide-react";

/**
 * About Us — updated per user's Feb 2026 brief.
 * Section order (per spec):
 *   1. Hero banner
 *   2. What Makes Us Unique?
 *   3. About Samara Wiz
 *   4. Our Mission
 *   5. Our Vision
 *   6. Director's Message
 * Content sourced verbatim from
 * SamaraWiz_About_Us_and_SmartOpportunity_Website_Content.docx
 */
export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <PageBanner
        label="About Us"
        title="Better Choices. Practical Learning. Meaningful Opportunities."
        subtitle="Samara Wiz brings Health, Education and Personal Care together through useful products, practical learning and an associate-led business opportunity."
        crumbs={[{ label: "Home", to: "/" }, { label: "About Us" }]}
      />

      {/* ============ 2 · WHAT MAKES US UNIQUE? ============ */}
      <section className="bg-ivory py-20 border-b border-line">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">01</div>
          <h2
            data-testid="about-unique-title"
            className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-royal leading-tight tracking-tight max-w-3xl"
          >
            What makes us <span className="text-gold-deep">Unique?</span>
          </h2>
          <p className="mt-5 text-navy/70 max-w-3xl leading-relaxed">
            Five pillars that distinguish Samara Wiz from every other
            direct-selling company — and translate into everyday value for our
            customers and associates.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
            {[
              {
                icon: Sparkles,
                l: "Everyday-Life Categories",
                d: "Health, wellness, personal care, beauty, fashion, education and digital learning — all under one platform.",
              },
              {
                icon: Award,
                l: "Value-Oriented Products",
                d: "Products and services designed around practical customer needs and long-term usefulness.",
              },
              {
                icon: Users,
                l: "Associate-Led Sales Model",
                d: "Retailing, referrals, customer service and team building — supported at every step.",
              },
              {
                icon: TrendingUp,
                l: "Structured Earnings",
                d: "Earning components based on eligible activity and the official company compensation plan.",
              },
              {
                icon: Heart,
                l: "Community Approach",
                d: "Learning, leadership, recognition and shared progress for every customer and associate.",
              },
            ].map((c) => (
              <div
                key={c.l}
                data-testid={`unique-${c.l.split(" ")[0].toLowerCase()}`}
                className="p-6 bg-white border border-line rounded-sm hover:border-gold/50 hover:shadow-md transition-all"
              >
                <div className="w-11 h-11 grid place-items-center bg-royal/[0.06] border border-royal/15 rounded-sm">
                  <c.icon size={20} className="text-royal" />
                </div>
                <div className="font-serif text-lg text-royal mt-4 leading-snug">
                  {c.l}
                </div>
                <p className="text-xs text-navy/65 mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 3 · ABOUT SAMARA WIZ ============ */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">02</div>
        <h2
          data-testid="about-samarawiz-title"
          className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-royal leading-tight tracking-tight"
        >
          About <span className="text-gold-deep">Samara Wiz</span>
        </h2>
        <div className="mt-6 space-y-5 text-royal/80 leading-relaxed">
          <p>
            <strong className="text-royal">Samara Wiz Pvt. Ltd.</strong> is a
            diversified products and services company focused on Health &amp;
            Wellness, Personal &amp; Beauty Care, Fashion &amp; Lifestyle, and
            Education &amp; Digital Learning. Our model combines customer-focused
            retailing with an associate opportunity supported by product
            knowledge, sales training, customer service and a clearly defined
            compensation plan.
          </p>
          <p>
            We believe sustainable growth begins with real customer value.
            Therefore, our associates are encouraged to understand the products,
            recommend them responsibly, support customers after purchase and
            build their business through consistent sales and team development.
          </p>
        </div>
      </section>

      {/* ============ 4 · OUR MISSION ============ */}
      <section className="bg-royal text-white py-20 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/15 rounded-full blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">03</div>
          <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
            <div className="w-14 h-14 grid place-items-center bg-gold text-royal rounded-sm">
              <Compass size={24} />
            </div>
            <div>
              <h2
                data-testid="about-mission-title"
                className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight"
              >
                Our <span className="text-gold">Mission</span>
              </h2>
              <p className="mt-5 text-white/90 leading-relaxed text-lg max-w-3xl">
                To provide quality and value-oriented products, expand practical
                education and digital learning, support customer satisfaction,
                and create a transparent business opportunity for associates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 5 · OUR VISION ============ */}
      <section className="bg-ivory py-20 border-b border-line">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">04</div>
          <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
            <div className="w-14 h-14 grid place-items-center bg-royal text-gold border border-royal/20 rounded-sm">
              <Eye size={24} />
            </div>
            <div>
              <h2
                data-testid="about-vision-title"
                className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-royal leading-tight tracking-tight"
              >
                Our <span className="text-gold-deep">Vision</span>
              </h2>
              <p className="mt-5 text-royal/85 leading-relaxed text-lg max-w-3xl">
                To build a trusted Samara Wiz community that helps people
                improve everyday life through better health choices, personal
                care, learning and entrepreneurship.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 6 · DIRECTOR'S MESSAGE ============ */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">05</div>
        <h2
          data-testid="about-director-title"
          className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-royal leading-tight tracking-tight"
        >
          Director&rsquo;s <span className="text-gold-deep">Message</span>
        </h2>

        <div
          data-testid="about-director-message"
          className="mt-10 relative p-8 md:p-12 bg-ivory border-l-4 border-gold rounded-sm"
        >
          <Quote size={32} className="absolute -top-3 -left-3 bg-royal text-gold p-1.5 rounded-sm" />

          <p className="font-serif text-xl text-royal/90 leading-relaxed italic">
            &ldquo;Samara Wiz is committed to serving with honesty, enthusiasm
            and care.&rdquo;
          </p>

          <div className="mt-6 space-y-4 text-royal/80 leading-relaxed">
            <p>
              We aim to build a reputable platform where product quality,
              customer value, continuous learning and associate uplift go
              together. We appreciate every customer and associate who becomes
              part of the Samara Wiz journey.
            </p>
            <p>
              As we grow, our focus will remain on better products, clear
              policies, regular support and opportunities that encourage people
              to learn, grow and progress together.
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-line flex items-center gap-4">
            <div className="w-14 h-14 grid place-items-center bg-royal text-gold rounded-full font-serif text-lg">
              SW
            </div>
            <div>
              <div className="font-serif text-lg text-royal">
                The Director
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold-deep mt-0.5">
                Samara Wiz Pvt. Ltd.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-navy text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-14 h-14 grid place-items-center bg-gold text-royal rounded-sm mx-auto">
            <GraduationCap size={22} />
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl mt-6 leading-tight">
            Ready to be part of the Samara Wiz journey?
          </h3>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/register"
              data-testid="about-cta-register-bottom"
              className="bg-gold text-royal px-7 py-3.5 rounded-sm hover:bg-white transition-colors inline-flex items-center gap-2 font-medium"
            >
              Register as an Associate <ArrowRight size={16} />
            </Link>
            <Link
              to="/opportunity"
              className="border-2 border-white text-white px-7 py-3.5 rounded-sm hover:bg-white hover:text-navy transition-colors inline-flex items-center gap-2"
            >
              Explore Smart Opportunity
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
